import { useMemo, useState, type ReactElement } from 'react';

import styles from './palmares-table.module.scss';
import { PalmaresRow } from './palmares-row/palmares-row';
import { Font } from '@utmb/design-system/text/font';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';
import { PalmaresFilter } from './palmares-filter/palmares-filter';
import { IndexSwitcher } from '../index-switcher/index-switcher';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { PalmaresSearch } from './palmares-search/palmares-search';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Link } from '@utmb/design-system/link/link';
import { IconsEnum } from '@utmb/types/icons';
import { PalmaresPodiumFilter, type PodiumFilterValue } from './palmares-podium-filter/palmares-podium-filter';
import { useTranslation } from 'next-i18next';

export type PalmaresSortKey = 'date' | 'race' | 'score' | 'distance' | 'time' | 'scratch' | 'gender';
export type PalmaresSortDirection = 'asc' | 'desc';
export type PalmaresSortState = { key: PalmaresSortKey; direction: PalmaresSortDirection };

export interface PalmaresTableProps {
    results: RaceYearResultForRunnerView[];
    isAuthenticated: boolean;
    isMe?: boolean;
    onDeleteResult?: (uri: string) => void;
}

function compareFn(a: RaceYearResultForRunnerView, b: RaceYearResultForRunnerView, key: PalmaresSortKey): number {
    switch (key) {
        case 'date':
            return new Date(a.dateIso).getTime() - new Date(b.dateIso).getTime();
        case 'race':
            return (a.raceName ?? '').localeCompare(b.raceName ?? '');
        case 'score':
            return a.index - b.index;
        case 'distance':
            return a.distance - b.distance;
        case 'time':
            return (a.time ?? '').localeCompare(b.time ?? '');
        case 'scratch':
            return a.rank - b.rank;
        case 'gender':
            return a.rankGender - b.rankGender;
        default:
            return 0;
    }
}

export function PalmaresTable({ results, isAuthenticated, isMe = false, onDeleteResult }: PalmaresTableProps): ReactElement {
    const [selectedCategory, setSelectedCategory] = useState<PiCategoryEnum>(PiCategoryEnum.GENERAL);
    const [sortState, setSortState] = useState<PalmaresSortState>({ key: 'date', direction: 'desc' });
    const [search, setSearch] = useState('');
    const [podiumFilter, setPodiumFilter] = useState<PodiumFilterValue>([]);
    const { t } = useTranslation();

    function handleSort(key: PalmaresSortKey) {
        setSortState((prev) => {
            if (prev?.key !== key) return { key, direction: 'desc' };
            return { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
        });
    }

    const validResults = useMemo(() => {
        const searchLower = search.toLowerCase().trim();
        return results
            .filter((r) => r.dateIso)
            .filter((r) => selectedCategory === PiCategoryEnum.GENERAL || r.piCategory === selectedCategory)
            .filter((r) => !searchLower || (r.raceName ?? '').toLowerCase().includes(searchLower) || (r.eventName ?? '').toLowerCase().includes(searchLower))
            .filter((r) => podiumFilter.length === 0 || podiumFilter.includes(r.rankGender));
    }, [results, selectedCategory, search, podiumFilter]);

    // When sorting by date and no active search → group by year.
    const groupByYear = sortState.key === 'date' && !search.trim();

    const resultsByYear = useMemo(() => {
        if (!groupByYear) return [];

        const grouped = new Map<number, RaceYearResultForRunnerView[]>();
        for (const r of validResults) {
            const year = new Date(r.dateIso).getFullYear();
            const list = grouped.get(year) ?? [];
            list.push(r);
            grouped.set(year, list);
        }

        const dateDir = sortState?.direction ?? 'desc';
        const dateMul = dateDir === 'desc' ? 1 : -1;

        return Array.from(grouped.entries())
            .sort(([a], [b]) => (b - a) * dateMul)
            .map(([year, races]) => ({
                year,
                races: races.sort((a, b) => dateMul * (new Date(b.dateIso).getTime() - new Date(a.dateIso).getTime())),
            }));
    }, [validResults, groupByYear, sortState]);

    const flatSorted = useMemo(() => {
        if (groupByYear || !sortState) return [];

        const sorted = [...validResults].sort((a, b) => compareFn(a, b, sortState.key));
        return sortState.direction === 'desc' ? sorted.reverse() : sorted;
    }, [validResults, groupByYear, sortState]);

    return (
        <div className={styles.palmaresTable}>
            <div className={styles.wrapHeader}>
                <div className={styles.wrapTitle}>
                    <Font.FuturaMedium as="h3" desktop="64" mobile="28" className={styles.title} color={getColorStyle(ColorTypesEnum.PRIMARY)}>
                        {results.length > 1 ? t('common.result.plural') : t('common.result.singular')}
                    </Font.FuturaMedium>
                    <div className={styles.links}>
                        <Link className={styles.link} href="/race-score-utmb-index" target="_blank">
                            <Font.FuturaBook desktop="16" mobile="14">
                                {t('singleRunner.statistics.global.howScoreCalculated')}
                            </Font.FuturaBook>
                        </Link>
                        <Link className={styles.link} href="/utmb-index/results-info" target="_blank">
                            <Font.FuturaBook desktop="16" mobile="14">
                                {t('singleRunner.statistics.global.whyIsntResultAppearing')}
                            </Font.FuturaBook>
                        </Link>
                    </div>
                </div>
                <div className={styles.header}>
                    <IndexSwitcher isDark value={selectedCategory} onChange={setSelectedCategory} />
                    <div className={styles.wrapSearchPodiumFilter}>
                        <PalmaresSearch value={search} onChange={setSearch} />
                        <PalmaresPodiumFilter value={podiumFilter} onChange={setPodiumFilter} />
                    </div>
                </div>
            </div>

            <PalmaresFilter sortState={sortState} onSort={handleSort} hasDeleteColumn={isMe && !!onDeleteResult} isAuthenticated={isAuthenticated} />
            <div className={styles.wrapData}>
                {validResults.length === 0 ? (
                    <div className={styles.noResult}>
                        <Icon variant={IconsEnum.CLOSE} width={24} height={24} color={getColorStyle(ColorTypesEnum.GREY_TEXT)} />
                        <Font.FuturaBook className={styles.noResult} desktop="20" mobile="20" color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                            {t('racesList.noResults')}
                        </Font.FuturaBook>
                    </div>
                ) : groupByYear ? (
                    resultsByYear.map(({ year, races }) => (
                        <div key={year} className={styles.wrapYear}>
                            <Font.FuturaMedium className={styles.year} desktop="32" mobile="32" color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                                {year}
                            </Font.FuturaMedium>
                            <div className={styles.table}>
                                {races.map((r) => (
                                    <PalmaresRow key={r.raceYearId} result={r} isAuthenticated={isAuthenticated} isMe={isMe} onDelete={onDeleteResult} />
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={styles.wrapYear}>
                        <div className={styles.table}>
                            {flatSorted.map((r) => (
                                <PalmaresRow key={r.raceYearId} result={r} isAuthenticated={isAuthenticated} isMe={isMe} onDelete={onDeleteResult} showYear />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
